import { ActionIcon, Drawer, Group, Stack, Title } from '@mantine/core';
import {
    ContentParentRelation,
    ContentResource,
    defaultSubtypedContentResource,
    useContentActions,
} from '../../../resource/resources/artifacts/content/contentResource';
import { Show } from '../../../../../react-commons/components/control-flow/Show';
import { ContentView } from './ContentView';
import { OpenModal } from '../../../../../react-commons/utils/modals/openModal';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { useDeleteResourceDialogFunction } from '../../reusable-components/dialogs/UseDeleteResourceDialogFunction';
import { ResourceType } from '../../../resource/enumerations/ResourceType';
import { ContentSectionContent } from './ContentSectionContent';
import { useParams, useSearchParams } from 'react-router-dom';
import { QueryBaseComponent } from '../../../../../react-commons/query/QueryBaseComponent';
import { BPMNModelerViewer } from '../process-area/activity/BPMNModelerViewer';
import { useUserId } from '../../authentication/useUserId';
import { useUserActions } from '../../../resource/resources/persistables/user/userResource';
import { useActivitiesActions } from '../../../resource/resources/artifacts/activities/activitiesResource';
import { useProjectVersionActions } from '../../../resource/resources/artifacts/project/projectVersionResource';

export const ActivitiesDrawerContentActivitiesRefWrapper= ({ openedDrawer, closeDrawer, activitiesRefObjectId }: {
    openedDrawer: boolean;
    closeDrawer: () => void;
    activitiesRefObjectId: string;
}) => {
    const {
        id: projectId,
        versionId,
        standardId, useCaseId, scopeId,
    } = useParams<{
        id: string,
        versionId: string,
        standardId: string, useCaseId: string, scopeId: string
    }>();

    const userId = useUserId('');
    const [search] = useSearchParams();

    const userActions = useUserActions();
    const activitiesActions = useActivitiesActions();
    const projectVersionActions = useProjectVersionActions();
    const userSystemRights = userActions.useGetUserEffectiveRightsOfUser(userId, projectId);

    const projectVersionQuery = projectVersionActions.useGetByPublishment(search.get('publishment'), projectId);

    const getVersionId = () => versionId ?? projectVersionQuery.data?.id;

    const activitiesQuery = activitiesActions.useGetResourceByObjectIdAndVersionedIn(activitiesRefObjectId, getVersionId());
    const isAdminOrServerAdmin =
        Boolean(userSystemRights.data && (userSystemRights.data.isProcessAdmin) && !search.get('publishment'));

    const onEdit = () => {
        const urlPostfix = standardId && useCaseId && scopeId ? `/standard/${standardId}/use-case/${useCaseId}/scope/${scopeId}` : '';
        const id = activitiesQuery.data?.id;
        const urlBase = window.location.origin;
        if (id) {
            window.open(`${urlBase}/project/${projectId}/active-version/${getVersionId()}/activities-model/${id}${urlPostfix}`);
        }
    };

    const getTitle = () => {
        return 'Referenced Activities Model';
    };

    return (
        <>
            <Drawer
                opened={openedDrawer}
                onClose={closeDrawer}
                overlayProps={{ backgroundOpacity: 0.5, blur: 4 }}
                title={getTitle()}
                position={'right'}
                size={'75%'}
                padding={25}
            >
                <QueryBaseComponent name="Detailed Design Model" query={activitiesQuery}>
                    {(data) => (
                        <BPMNModelerViewer
                            style={{
                                height: 'calc(100vh - 50px)',
                            }}
                            activityId={data.id}
                            onAdd={undefined}
                            link={undefined}
                            onDelete={undefined}
                            onEdit={isAdminOrServerAdmin ? onEdit : undefined}
                        />
                    )}
                </QueryBaseComponent>
            </Drawer>
        </>
    );
};


export const ActivitiesDrawerContent= ({
          openedDrawer,
          closeDrawer,
          activitiesModelId,
          activitiesRefObjectId,
          elementId,
          elementTitle,
          contentResource,
          readOnly,
      }: {
    openedDrawer: boolean;
    closeDrawer: () => void;
    activitiesModelId: string;
    activitiesRefObjectId: string | undefined;
    elementId: string;
    elementTitle: string | null;
    contentResource: ContentResource | null;
    readOnly: boolean;
}) => {
    const deleteResourceDialogFunction = useDeleteResourceDialogFunction();
    const contentActions = useContentActions();

    const addContent = contentActions.useAddResource();
    const deleteContent = contentActions.useDeleteResource();

    const getTitle = () => {
        if (readOnly) {
            return (
                <Title order={3}>
                    {elementTitle ?? contentResource?.name}
                </Title>
            );
        } else if (!contentResource) {
            return (
                <Group>
                    <Title order={3}>No Content Assigned to this Element</Title>
                    <ActionIcon
                        variant={'transparent'}
                        color={'var(--mantine-color-base-button)'}
                        onClick={() => {
                            OpenModal.StringInputModal(
                                'Create new Content',
                                async (value) => {
                                    await addContent.mutateAsync({
                                        resource: {
                                            ...defaultSubtypedContentResource,
                                            name: value,
                                            modelElement: elementId,
                                        },
                                        parentRelation: ContentParentRelation.ACTIVITIES_MODEL,
                                        parentId: activitiesModelId,
                                    });
                                },
                            );
                        }}
                    >
                        <IconPlus />
                    </ActionIcon>
                </Group>
            );
        } else {
            return (
                <Group>
                    <Title
                        order={3}>{elementTitle ? `${contentResource.name} (${elementTitle})` : contentResource.name}</Title>
                    <ActionIcon
                        variant={'transparent'}
                        color={'var(--mantine-color-base-button)'}
                        onClick={() => {
                            deleteResourceDialogFunction.deleteResource(contentResource.id, contentResource.name, ResourceType.CONTENT, deleteContent);
                        }}
                    >
                        <IconTrash />
                    </ActionIcon>
                </Group>
            );
        }
    };

    if (elementId.startsWith('Activities_Reference') && activitiesRefObjectId) {
        return <ActivitiesDrawerContentActivitiesRefWrapper openedDrawer={openedDrawer} closeDrawer={closeDrawer}
                                                            activitiesRefObjectId={activitiesRefObjectId} />;
    }

    return (
        <>
            <Drawer
                opened={openedDrawer}
                onClose={closeDrawer}
                overlayProps={{ backgroundOpacity: 0.5, blur: 4 }}
                title={getTitle()}
                position={'right'}
                size={'75%'}
                padding={25}
            >
                <Stack>
                    <Show when={contentResource?.id && !readOnly}>
                        <ContentView id={contentResource?.id} />
                    </Show>
                    <Show when={contentResource?.id}>
                        <ContentSectionContent contentId={contentResource?.id ?? ''} readOnly={readOnly} />
                    </Show>
                </Stack>
            </Drawer>
        </>
    );
};
